
import { User, BackendQuestion, ExamSession, SimulatorScenario, ClinicalSimulatorScenario, Podcast, StudyModule, ExamConfig, CertificationType, SkillSlide, ScenarioChallenge, Promo, PageSeoConfig, Flashcard, TwoFactorStatus, TrustedDeviceInfo } from '../types';
import { DEFAULT_CLINICAL_SIMULATORS } from '../data/clinicalSimulatorData';
import { CLINICAL_ENVIRONMENTS, coords2DtoSpherical } from '../data/clinicalEnvironments';
import { CNA_SKILLS } from '../data/skillsData';
import { GuestTrialService } from './guestTrialService';

export const isSkillHydrated = (skill: SkillSlide | null | undefined): boolean => {
    if (!skill || !Array.isArray(skill.slides) || skill.slides.length === 0) return false;
    return skill.slides.some(sl => {
        if (!sl || !sl.data) return false;
        const d = sl.data;
        if (sl.type === 'procedure' && Array.isArray(d.steps) && d.steps.length > 0) return true;
        if (sl.type === 'supplies' && ((Array.isArray(d.items) && d.items.length > 0) || (Array.isArray(d.supplies) && d.supplies.length > 0))) return true;
        if (sl.type === 'overview' && (d.importance || d.purpose || d.description || d.examContext || d.safetyFocus)) return true;
        if (sl.type === 'infographic' && (d.imageUrl || d.visualAlt || (Array.isArray(d.observations) && d.observations.length > 0))) return true;
        if (sl.type === 'criticals' && Array.isArray(d.items) && d.items.length > 0) return true;
        if (sl.type === 'reporting' && (d.instruction || d.sampleWording)) return true;
        if (sl.type === 'completion' && (Array.isArray(d.items) || Array.isArray(d.steps) || d.summary)) return true;
        return false;
    });
};

export const API_BASE_URL = '/practice-exams/api';

export type UserProgress = User;

const cleanParams = (params: Record<string, any>) => {
    const cleaned: Record<string, any> = {};
    Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
            cleaned[key] = params[key];
        }
    });
    return cleaned;
};

export async function safeParseJson(response: Response) {
    const text = await response.text();
    try {
        return JSON.parse(text);
    } catch (e) {
        console.error("JSON Parse Failure at length:", text.length);
        if (text.trim().startsWith('<!DOCTYPE html>') || text.trim().startsWith('<html')) {
            throw new Error(`Server returned HTML instead of JSON (Status ${response.status}). This usually indicates a Route Collision or Gateway Timeout.`);
        }
        if (text.length > 0 && !text.trim().endsWith('}') && !text.trim().endsWith(']')) {
            throw new Error(`Incomplete Data: Registry payload was truncated (Length: ${(text.length / 1024 / 1024).toFixed(2)}MB). Check network stability.`);
        }
        throw new Error(`Malformed Registry Data: ${e instanceof Error ? e.message : 'Unknown format'}`);
    }
}

export const SecurityService = {
  secureValue: async (value: string): Promise<string> => {
    return value;
  }
};

export function normalizeClinicalSimulatorScenario(raw: any, certId: string = 'cna'): ClinicalSimulatorScenario {
    const targetCertId = (raw?.certId || certId || 'cna').toLowerCase().trim();
    const getEnvPhotosphere = (envKey?: string) => {
        if (!envKey) return '/images/environments/bedside_room_360.jpg';
        try {
            if (typeof CLINICAL_ENVIRONMENTS !== 'undefined' && CLINICAL_ENVIRONMENTS[envKey]) {
                return CLINICAL_ENVIRONMENTS[envKey].photosphereUrl || '/images/environments/bedside_room_360.jpg';
            }
        } catch (e) {}
        return '/images/environments/bedside_room_360.jpg';
    };

    const s = raw || {};
    const patientRaw = s.patient || {};
    const vitalsRaw = patientRaw.baselineVitals || {};
    const rubricRaw = s.rubricMilestones || {};

    const normalized: ClinicalSimulatorScenario = {
        id: String(s.id || `sim_${targetCertId}_${Date.now()}`),
        skillId: String(s.skillId || ''),
        certId: targetCertId,
        version: Number(s.version) || 1,
        status: s.status === 'published' ? 'published' : (s.status === 'testing' ? 'testing' : (s.status === 'archived' ? 'archived' : 'draft')),
        title: String(s.title || 'Untitled Clinical Simulation'),
        description: String(s.description || 'Step-by-step interactive clinical examination simulation.'),
        environment: s.environment || 'bedside_room',
        customImageUrl: s.customImageUrl || '',
        imageUrl: s.imageUrl || '',
        enable3dPhotosphere: s.enable3dPhotosphere !== false,
        photosphereUrl: s.photosphereUrl || getEnvPhotosphere(s.environment) || s.imageUrl || '/images/environments/bedside_room_360.jpg',
        defaultCameraHeading: typeof s.defaultCameraHeading === 'number' ? s.defaultCameraHeading : 0,
        patient: {
            name: String(patientRaw.name || 'Resident / Patient'),
            age: typeof patientRaw.age === 'number' ? patientRaw.age : (parseInt(patientRaw.age) || 72),
            roomNumber: String(patientRaw.roomNumber || 'Room 101'),
            primaryDiagnosis: String(patientRaw.primaryDiagnosis || 'Standard Clinical Care'),
            mobilityStatus: String(patientRaw.mobilityStatus || 'Supervision / Standby assist'),
            precautions: Array.isArray(patientRaw.precautions) ? patientRaw.precautions.filter(Boolean) : ['Standard Precautions'],
            baselineVitals: {
                bp: String(vitalsRaw.bp || '120/80 mmHg'),
                hr: typeof vitalsRaw.hr === 'number' ? vitalsRaw.hr : (parseInt(vitalsRaw.hr) || 74),
                rr: typeof vitalsRaw.rr === 'number' ? vitalsRaw.rr : (parseInt(vitalsRaw.rr) || 16),
                spo2: typeof vitalsRaw.spo2 === 'number' ? vitalsRaw.spo2 : (parseInt(vitalsRaw.spo2) || 98),
                temp: String(vitalsRaw.temp || '98.6°F')
            }
        },
        rubricMilestones: {
            infectionControl: {
                totalWeight: typeof rubricRaw.infectionControl?.totalWeight === 'number' ? rubricRaw.infectionControl.totalWeight : 20,
                requiredActions: Array.isArray(rubricRaw.infectionControl?.requiredActions) ? rubricRaw.infectionControl.requiredActions : []
            },
            residentDignity: {
                totalWeight: typeof rubricRaw.residentDignity?.totalWeight === 'number' ? rubricRaw.residentDignity.totalWeight : 20,
                requiredActions: Array.isArray(rubricRaw.residentDignity?.requiredActions) ? rubricRaw.residentDignity.requiredActions : []
            },
            safetyMechanics: {
                totalWeight: typeof rubricRaw.safetyMechanics?.totalWeight === 'number' ? rubricRaw.safetyMechanics.totalWeight : 25,
                requiredActions: Array.isArray(rubricRaw.safetyMechanics?.requiredActions) ? rubricRaw.safetyMechanics.requiredActions : []
            },
            proceduralTechnique: {
                totalWeight: typeof rubricRaw.proceduralTechnique?.totalWeight === 'number' ? rubricRaw.proceduralTechnique.totalWeight : 35,
                requiredActions: Array.isArray(rubricRaw.proceduralTechnique?.requiredActions) ? rubricRaw.proceduralTechnique.requiredActions : []
            }
        },
        instantFailTraps: Array.isArray(s.instantFailTraps) ? s.instantFailTraps.map((t: any, idx: number) => ({
            id: String(t.id || `trap_${idx}_${Date.now()}`),
            name: String(t.name || `Critical Error ${idx + 1}`),
            triggerCondition: String(t.triggerCondition || ''),
            boardRationale: String(t.boardRationale || 'Critical state board safety or infection control violation.')
        })) : [],
        hotspots: Array.isArray(s.hotspots) ? s.hotspots.map((h: any, hIdx: number) => {
            const envDef = CLINICAL_ENVIRONMENTS[s.environment || 'bedside_room'] || CLINICAL_ENVIRONMENTS.bedside_room;
            const zoneKey = h.zone || 'bed';
            const defCoords = envDef?.defaultHotspotCoordinates?.[zoneKey] || { x: 50, y: 50 };
            const defSpherical = envDef?.defaultSphericalCoordinates?.[zoneKey] || coords2DtoSpherical(defCoords.x, defCoords.y);

            const coords = (h.coordinates && typeof h.coordinates.x === 'number' && typeof h.coordinates.y === 'number')
                ? { x: Number(h.coordinates.x), y: Number(h.coordinates.y) }
                : { x: defCoords.x, y: defCoords.y };

            const spherical = (h.sphericalCoordinates && typeof h.sphericalCoordinates.yaw === 'number' && typeof h.sphericalCoordinates.pitch === 'number')
                ? { yaw: Number(h.sphericalCoordinates.yaw), pitch: Number(h.sphericalCoordinates.pitch) }
                : { yaw: defSpherical.yaw, pitch: defSpherical.pitch };

            return {
                id: String(h.id || `hotspot_${hIdx}_${Date.now()}`),
                name: String(h.name || `Station ${hIdx + 1}`),
                zone: zoneKey,
                requiredPhase: h.requiredPhase || 'any',
                coordinates: coords,
                sphericalCoordinates: spherical,
                availableActions: Array.isArray(h.availableActions) ? h.availableActions.map((a: any, aIdx: number) => ({
                    actionId: String(a.actionId || `act_${hIdx}_${aIdx}`),
                    label: String(a.label || `Action ${aIdx + 1}`),
                    feedbackDialogue: String(a.feedbackDialogue || 'Action performed.'),
                    isCritical: !!a.isCritical,
                    pointsAwarded: typeof a.pointsAwarded === 'number' ? a.pointsAwarded : 10,
                    category: a.category || 'technique',
                    stateTransitions: a.stateTransitions || {},
                    disqualifiesIfTriggered: !!a.disqualifiesIfTriggered,
                    disqualificationReason: String(a.disqualificationReason || '')
                })) : []
            };
        }) : [],
        activeSafetyTags: Array.isArray(s.activeSafetyTags) ? s.activeSafetyTags : undefined,
        initialState: {
            curtainDrawn: !!s.initialState?.curtainDrawn,
            bedHeight: s.initialState?.bedHeight || 'low',
            bedLocked: s.initialState?.bedLocked !== false,
            handsClean: !!s.initialState?.handsClean,
            glovesOn: !!s.initialState?.glovesOn,
            nonSkidShoesOn: !!s.initialState?.nonSkidShoesOn,
            callLightInReach: !!s.initialState?.callLightInReach,
            residentComfort: typeof s.initialState?.residentComfort === 'number' ? s.initialState.residentComfort : 100,
            ...(s.initialState || {})
        },
        createdAt: s.createdAt || new Date().toISOString(),
        updatedAt: s.updatedAt || new Date().toISOString()
    };

    return normalized;
}

export const StorageService = {
    _userCache: new Map<string, User>(),

    normalizeClinicalSimulatorScenario: (raw: any, certId?: string) => normalizeClinicalSimulatorScenario(raw, certId),

    getAuthHeader: () => {
        const token = window.localStorage.getItem('wix_auth_token') || ''; 
        const deviceToken = window.localStorage.getItem('admin_trusted_device_token') || '';
        const headers: Record<string, string> = { 
            'Content-Type': 'application/json', 
            'Authorization': token ? `Bearer ${token}` : '' 
        };
        if (deviceToken) {
            headers['x-device-token'] = deviceToken;
        }
        return headers;
    },

    getRegistrySummary: async (certId: string): Promise<any> => {
        const res = await fetch(`${API_BASE_URL}/${certId}/registry-summary`);
        if (!res.ok) throw new Error("Registry summary offline");
        return await safeParseJson(res);
    },

    // --- FLASHCARDS ---
    getFlashcards: async (certId: string, params: Record<string, any> = {}): Promise<any> => {
        const cleaned = cleanParams(params);
        const query = new URLSearchParams(cleaned).toString();
        const res = await fetch(`${API_BASE_URL}/${certId}/flashcards?${query}`);
        if (!res.ok) throw new Error("Flashcard Registry Offline");
        return await safeParseJson(res);
    },

    getFlashcardCategories: async (certId: string): Promise<{ categories: string[] }> => {
        try {
            const res = await fetch(`${API_BASE_URL}/${certId}/flashcards/categories`);
            if (!res.ok) return { categories: [] };
            return await safeParseJson(res);
        } catch {
            return { categories: [] };
        }
    },

    saveFlashcardsBulk: async (certId: string, flashcards: Partial<Flashcard>[]) => {
        const res = await fetch(`${API_BASE_URL}/${certId}/flashcards/bulk`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ flashcards })
        });
        if (!res.ok) throw new Error("Bulk Flashcard Commit Failed");
        return await safeParseJson(res);
    },

    updateFlashcard: async (certId: string, id: string, data: Partial<Flashcard>) => {
        const res = await fetch(`${API_BASE_URL}/${certId}/flashcards/${id}`, {
            method: 'PUT',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Flashcard Update Failed");
        return await safeParseJson(res);
    },

    deleteFlashcardsBulk: async (certId: string, ids: string[]) => {
        const res = await fetch(`${API_BASE_URL}/${certId}/flashcards/bulk`, {
            method: 'DELETE',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ ids })
        });
        if (!res.ok) throw new Error("Bulk Flashcard Purge Failed");
        return await safeParseJson(res);
    },

    deleteFlashcard: async (certId: string, id: string) => {
        await fetch(`${API_BASE_URL}/${certId}/flashcards/${id}`, {
            method: 'DELETE',
            headers: StorageService.getAuthHeader()
        });
    },

    // --- SEO MANAGEMENT ---
    getAllSeoConfigs: async (certId: string = 'global'): Promise<PageSeoConfig[]> => {
        const res = await fetch(`${API_BASE_URL}/admin/seo?certId=${certId}`, { headers: StorageService.getAuthHeader() });
        if (!res.ok) throw new Error("SEO Registry Offline");
        return await safeParseJson(res);
    },

    getPageSeoConfig: async (path: string, certId: string = 'global'): Promise<PageSeoConfig | null> => {
        const res = await fetch(`${API_BASE_URL}/seo/page?path=${encodeURIComponent(path)}&certId=${certId}`);
        if (!res.ok) return null;
        return await safeParseJson(res);
    },

    saveSeoConfig: async (config: Partial<PageSeoConfig>): Promise<PageSeoConfig> => {
        const res = await fetch(`${API_BASE_URL}/admin/seo`, {
            method: 'PUT',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify(config)
        });
        if (!res.ok) throw new Error("SEO Commit Failed");
        return await safeParseJson(res);
    },

    generateSeoDescription: async (path: string, currentTitle: string, certName: string): Promise<{ text: string }> => {
        const res = await fetch(`${API_BASE_URL}/admin/seo/generate`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ path, currentTitle, certName })
        });
        if (!res.ok) throw new Error("AI SEO Generator Offline");
        return await safeParseJson(res);
    },

    generateSeoSuite: async (path: string, certId: string): Promise<{ title: string; description: string; keywords: string[]; headerHierarchy: { h1: string; h2s: string[] }; jsonLd: string }> => {
        const res = await fetch(`${API_BASE_URL}/admin/seo/generate-suite`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ path, certId })
        });
        if (!res.ok) throw new Error("Advanced AI SEO Suite Generator Offline");
        return await safeParseJson(res);
    },

    rebuildSitemap: async (): Promise<{ 
        success: boolean; 
        urlCount: number; 
        timestamp?: string;
        sitemapPath?: string;
        sitemapRootUrl?: string;
        llmPath?: string;
        llmRootUrl?: string;
        llmsPath?: string;
        llmsRootUrl?: string;
        robotsPath?: string;
        robotsRootUrl?: string;
    }> => {
        const res = await fetch(`${API_BASE_URL}/admin/seo/rebuild-sitemap`, {
            method: 'POST',
            headers: StorageService.getAuthHeader()
        });
        if (!res.ok) throw new Error("Sitemap Compiler Sync Failed");
        return await safeParseJson(res);
    },

    getLlmPreview: async (): Promise<{ content: string; customTemplate: string; activeCertCount: number; appName: string; timestamp: string }> => {
        const res = await fetch(`${API_BASE_URL}/admin/seo/llm-preview`, {
            headers: StorageService.getAuthHeader()
        });
        if (!res.ok) throw new Error("Failed to load llms.txt preview");
        return await safeParseJson(res);
    },

    saveLlmTemplate: async (template: string): Promise<{ success: boolean; message: string }> => {
        const res = await fetch(`${API_BASE_URL}/admin/seo/llm-template`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ template })
        });
        if (!res.ok) throw new Error("Failed to update LLM configuration");
        return await safeParseJson(res);
    },

    testLlmSuggestion: async (query: string): Promise<{
        query: string;
        answer: string;
        score: number;
        mentionsDiamond: boolean;
        urlMatches: string[];
        recommendations: string[];
        llmsTimestamp: string;
    }> => {
        const res = await fetch(`${API_BASE_URL}/admin/seo/test-llm-suggestion`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ query })
        });
        if (!res.ok) {
            const err = await safeParseJson(res).catch(() => ({}));
            throw new Error(err.error || "LLM Suggestion Test failed");
        }
        return await safeParseJson(res);
    },

    getSitemapPreview: async (): Promise<{ xml: string; urlCount: number; certCount: number; customCount: number; timestamp: string }> => {
        const res = await fetch(`${API_BASE_URL}/admin/seo/sitemap-preview`, {
            headers: StorageService.getAuthHeader()
        });
        if (!res.ok) throw new Error("Failed to load sitemap.xml preview");
        return await safeParseJson(res);
    },

    getRobotsPreview: async (): Promise<{ robotsTxt: string; aiBotsAllowed: string[]; timestamp: string }> => {
        const res = await fetch(`${API_BASE_URL}/admin/seo/robots-preview`, {
            headers: StorageService.getAuthHeader()
        });
        if (!res.ok) throw new Error("Failed to load robots.txt preview");
        return await safeParseJson(res);
    },

    batchGuestBoost: async (): Promise<{ success: boolean; updatedCount: number; sitemapUrlCount: number }> => {
        const res = await fetch(`${API_BASE_URL}/admin/seo/batch-guest-boost`, {
            method: 'POST',
            headers: StorageService.getAuthHeader()
        });
        if (!res.ok) throw new Error("Batch guest boost failed");
        return await safeParseJson(res);
    },

    // --- DATABASE INTELLIGENCE ---
    getDatabaseStats: async (): Promise<any> => {
        const res = await fetch(`${API_BASE_URL}/admin/db/stats`, { headers: StorageService.getAuthHeader() });
        if (!res.ok) throw new Error("Registry Telemetry Offline");
        return await safeParseJson(res);
    },

    getSystemLogs: async (): Promise<any[]> => {
        const res = await fetch(`${API_BASE_URL}/admin/db/logs`, { headers: StorageService.getAuthHeader() });
        if (!res.ok) return [];
        return await safeParseJson(res);
    },

    getDashboardStats: async (): Promise<any> => {
        const res = await fetch(`${API_BASE_URL}/admin/dashboard-stats`, { headers: StorageService.getAuthHeader() });
        if (!res.ok) throw new Error("Dashboard metrics offline");
        return await safeParseJson(res);
    },

    clearSystemLogs: async () => {
        await fetch(`${API_BASE_URL}/admin/db/logs`, { method: 'DELETE', headers: StorageService.getAuthHeader() });
    },

    // --- WIX MEDIA AUDIT & STORAGE MANAGEMENT ---
    getMediaAuditStatus: async (project?: string): Promise<any> => {
        const query = project ? `?project=${encodeURIComponent(project)}` : '';
        const res = await fetch(`${API_BASE_URL}/admin/media-audit/status${query}`, { headers: StorageService.getAuthHeader() });
        if (!res.ok) throw new Error("Failed to check Wix Media API status");
        return await safeParseJson(res);
    },

    syncSecretManager: async (project?: string): Promise<any> => {
        const res = await fetch(`${API_BASE_URL}/admin/media-audit/sync-secrets`, {
            method: 'POST',
            headers: { ...StorageService.getAuthHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ project })
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Sync failed' }));
            throw new Error(err.error || 'Failed to sync from Google Cloud Secret Manager');
        }
        return await safeParseJson(res);
    },

    configureMediaCredentials: async (params: { apiKey?: string; siteId?: string; accountId?: string; parentFolderId?: string; gcpProjectId?: string }): Promise<any> => {
        const res = await fetch(`${API_BASE_URL}/admin/media-audit/configure-credentials`, {
            method: 'POST',
            headers: { ...StorageService.getAuthHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify(params)
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Configuration failed' }));
            throw new Error(err.error || 'Failed to configure media credentials');
        }
        return await safeParseJson(res);
    },

    scanMediaAudit: async (): Promise<any> => {
        const res = await fetch(`${API_BASE_URL}/admin/media-audit/scan`, { headers: StorageService.getAuthHeader() });
        if (!res.ok) throw new Error("Media audit scan failed or database unreachable");
        return await safeParseJson(res);
    },

    migrateMediaRecord: async (params: { recordId: string; collection: string; field?: string; targetId?: string; certId?: string; slideIdx?: number; slideId?: string; trackIdx?: number; compress?: boolean }): Promise<any> => {
        const res = await fetch(`${API_BASE_URL}/admin/media-audit/migrate-one`, {
            method: 'POST',
            headers: { ...StorageService.getAuthHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify(params)
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Migration failed' }));
            throw new Error(err.error || 'Failed to migrate asset to Wix');
        }
        return await safeParseJson(res);
    },

    compressMediaRecord: async (params: { recordId: string; collection: string; field?: string; targetId?: string; certId?: string; slideIdx?: number; slideId?: string; trackIdx?: number; currentUrl?: string }): Promise<any> => {
        const res = await fetch(`${API_BASE_URL}/admin/media-audit/compress-one`, {
            method: 'POST',
            headers: { ...StorageService.getAuthHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify(params)
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Compression failed' }));
            throw new Error(err.error || 'Failed to compress and upload asset');
        }
        return await safeParseJson(res);
    },

    deleteMediaBuffer: async (params: { recordId: string; collection: string; field?: string; targetId?: string; certId?: string; slideIdx?: number; slideId?: string; trackIdx?: number }): Promise<any> => {
        const res = await fetch(`${API_BASE_URL}/admin/media-audit/delete-buffer`, {
            method: 'POST',
            headers: { ...StorageService.getAuthHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify(params)
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Delete failed' }));
            throw new Error(err.error || 'Failed to delete media buffer');
        }
        return await safeParseJson(res);
    },

    manualUploadMedia: async (params: { base64Data: string; fileName: string; mimeType?: string; mediaType?: string; compress?: boolean }): Promise<any> => {
        const res = await fetch(`${API_BASE_URL}/admin/media-audit/manual-upload`, {
            method: 'POST',
            headers: { ...StorageService.getAuthHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify(params)
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Upload failed' }));
            throw new Error(err.error || 'Failed to upload media to Wix CDN');
        }
        return await safeParseJson(res);
    },

    batchMigrateMedia: async (records: any[], compress: boolean = true): Promise<any> => {
        const res = await fetch(`${API_BASE_URL}/admin/media-audit/batch-migrate`, {
            method: 'POST',
            headers: { ...StorageService.getAuthHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ records, compress })
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Batch migration failed' }));
            throw new Error(err.error || 'Failed to complete batch migration');
        }
        return await safeParseJson(res);
    },

    getActivePromo: async (): Promise<Promo | null> => {
        const res = await fetch(`${API_BASE_URL}/promos/active`);
        if (!res.ok) return null;
        return await safeParseJson(res);
    },

    getAdminPromos: async (): Promise<Promo[]> => {
        const res = await fetch(`${API_BASE_URL}/promos/admin`, { headers: StorageService.getAuthHeader() });
        if (!res.ok) return [];
        return await safeParseJson(res);
    },

    savePromo: async (promo: Partial<Promo>) => {
        const method = promo._id ? 'PUT' : 'POST';
        const url = promo._id ? `${API_BASE_URL}/promos/admin/${promo._id}` : `${API_BASE_URL}/promos/admin`;
        const res = await fetch(url, {
            method,
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify(promo)
        });
        return await safeParseJson(res);
    },

    deletePromo: async (id: string) => {
        await fetch(`${API_BASE_URL}/promos/admin/${id}`, {
            method: 'DELETE',
            headers: StorageService.getAuthHeader()
        });
    },

    trackUserEvent: async (event: { action: string; category?: string; label?: string; value?: number; metadata?: any }) => {
        try {
            const guestSessionId = GuestTrialService.getSessionId();
            const authHeader = StorageService.getAuthHeader();
            await fetch(`${API_BASE_URL}/analytics/track`, {
                method: 'POST',
                headers: {
                    ...authHeader,
                    'X-Guest-Session-Id': guestSessionId,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...event,
                    guestSessionId
                })
            });
        } catch (e) {
            console.warn("Event telemetry skipped:", e);
        }
    },

    getSuperAdminAnalytics: async (startDate?: string, endDate?: string): Promise<any> => {
        let url = `${API_BASE_URL}/admin/super-analytics`;
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        const queryString = params.toString();
        if (queryString) {
            url += `?${queryString}`;
        }
        const res = await fetch(url, { headers: StorageService.getAuthHeader() });
        if (!res.ok) throw new Error("Super analytics telemetry offline");
        return await safeParseJson(res);
    },

    getUserJourneyAuditorData: async (
        days: number = 14,
        audienceType: 'all' | 'registered' | 'guest' = 'all',
        options?: {
            preset?: string;
            startDate?: string;
            endDate?: string;
            page?: number;
            limit?: number;
            sortOrder?: 'asc' | 'desc';
            registeredPage?: number;
            registeredLimit?: number;
            registeredSort?: 'asc' | 'desc';
            guestPage?: number;
            guestLimit?: number;
            guestSort?: 'asc' | 'desc';
        }
    ): Promise<any> => {
        const queryParams = new URLSearchParams({
            days: days.toString(),
            type: audienceType
        });

        if (options?.preset) queryParams.set('preset', options.preset);
        if (options?.startDate) queryParams.set('startDate', options.startDate);
        if (options?.endDate) queryParams.set('endDate', options.endDate);
        if (options?.limit) queryParams.set('limit', options.limit.toString());
        if (options?.page) queryParams.set('page', options.page.toString());
        if (options?.sortOrder) queryParams.set('sortOrder', options.sortOrder);
        if (options?.registeredPage) queryParams.set('registeredPage', options.registeredPage.toString());
        if (options?.registeredLimit) queryParams.set('registeredLimit', options.registeredLimit.toString());
        if (options?.registeredSort) queryParams.set('registeredSort', options.registeredSort);
        if (options?.guestPage) queryParams.set('guestPage', options.guestPage.toString());
        if (options?.guestLimit) queryParams.set('guestLimit', options.guestLimit.toString());
        if (options?.guestSort) queryParams.set('guestSort', options.guestSort);

        const url = `${API_BASE_URL}/admin/user-journey-auditor?${queryParams.toString()}`;
        const res = await fetch(url, { headers: StorageService.getAuthHeader() });
        if (!res.ok) throw new Error("User Journey Auditor database telemetry offline");
        return await safeParseJson(res);
    },

    getUserSessionActivityLog: async (userId: string): Promise<any> => {
        const url = `${API_BASE_URL}/admin/user/${encodeURIComponent(userId)}/session-activity-log`;
        const res = await fetch(url, { headers: StorageService.getAuthHeader() });
        if (!res.ok) throw new Error("First-session active telemetry dataset offline");
        return await safeParseJson(res);
    },

    getProgress: (userIdOrEmail: string): UserProgress => {
        return StorageService._userCache.get(userIdOrEmail) || {
            id: userIdOrEmail,
            role: 'student',
            planTier: 'free',
            history: [],
            gamification: { points: 0, level: 1, badges: [] },
            aiFeedbackLog: []
        } as User;
    },

    saveUser: async (user: User) => {
        const res = await fetch(`${API_BASE_URL}/users/${user.id || user.email}`, {
            method: 'PUT',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify(user)
        });
        if (!res.ok) {
            const data = await safeParseJson(res).catch(() => ({ error: 'User Synchronization Failure' }));
            throw new Error(data.error || 'User Synchronization Failure');
        }
        const updated = await safeParseJson(res);
        if (updated) {
          StorageService._userCache.set(updated.id, updated);
          if (updated.email) StorageService._userCache.set(updated.email, updated);
        }
        return updated;
    },

    deleteUser: async (id: string) => {
        await fetch(`${API_BASE_URL}/users/${id}`, {
            method: 'DELETE',
            headers: StorageService.getAuthHeader()
        });
    },

    adminToggleUser2FA: async (userId: string, enabled: boolean) => {
        const res = await fetch(`${API_BASE_URL}/auth/2fa/admin-toggle-user`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ userId, enabled })
        });
        if (!res.ok) {
            const err = await safeParseJson(res).catch(() => ({ error: 'Failed to update 2FA configuration' }));
            throw new Error(err.error || 'Failed to update 2FA configuration');
        }
        return await safeParseJson(res);
    },

    saveProgress: async (userId: string, data: Partial<User>) => {
        const current = StorageService.getProgress(userId);
        const updated = { ...current, ...data };
        StorageService._userCache.set(userId, updated);
        return await StorageService.saveUser(updated);
    },

    _certsCache: null as CertificationType[] | null,

    getCachedCertifications: (): CertificationType[] => {
        return StorageService._certsCache || [];
    },

    getCertifications: async (forceRefresh: boolean = false): Promise<CertificationType[]> => {
        if (!forceRefresh && StorageService._certsCache && StorageService._certsCache.length > 0) {
            return StorageService._certsCache;
        }
        const res = await fetch(`${API_BASE_URL}/certifications`);
        if (!res.ok) throw new Error("Registry unavailable");
        const data = await safeParseJson(res);
        const certs = data.items || [];
        StorageService._certsCache = certs;
        return certs;
    },

    saveCertification: async (cert: Partial<CertificationType>) => {
        const method = cert._id ? 'PUT' : 'POST';
        const url = cert._id ? `${API_BASE_URL}/certifications/${cert.id}` : `${API_BASE_URL}/certifications`;
        const res = await fetch(url, {
            method,
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify(cert)
        });
        if (!res.ok) {
            const err = await safeParseJson(res).catch(() => ({ error: 'Certification Save Protocol Failed' }));
            throw new Error(err.error || "Failed to commit certification logic.");
        }
        const saved = await safeParseJson(res);
        if (StorageService._certsCache) {
            const idx = StorageService._certsCache.findIndex(c => c.id === saved.id);
            if (idx >= 0) {
                StorageService._certsCache[idx] = saved;
            } else {
                StorageService._certsCache.push(saved);
            }
        }
        window.dispatchEvent(new CustomEvent('certifications-updated', { detail: saved }));
        return saved;
    },

    saveCertificationsBulk: async (certs: Partial<CertificationType>[]) => {
        const results = await Promise.all(
            certs.map(c => StorageService.saveCertification(c))
        );
        StorageService._certsCache = results;
        window.dispatchEvent(new CustomEvent('certifications-updated', { detail: results }));
        return results;
    },

    getQuestionCategories: async (certId: string, stateCode?: string): Promise<{ categories: { category: string; count: number }[] }> => {
        try {
            const query = stateCode && stateCode !== 'Global' && stateCode !== 'All' ? `?state=${encodeURIComponent(stateCode)}` : '';
            const res = await fetch(`${API_BASE_URL}/${certId}/questions/categories${query}`);
            if (!res.ok) throw new Error("Category search failed.");
            return await safeParseJson(res);
        } catch (err) {
            console.warn("Category endpoint error:", err);
            return { categories: [] };
        }
    },

    getQuestions: async (certId: string, params: Record<string, any> = {}): Promise<any> => {
        const cleaned = cleanParams(params);
        const query = new URLSearchParams(cleaned).toString();
        const res = await fetch(`${API_BASE_URL}/${certId}/questions?${query}`);
        if (!res.ok) throw new Error("Registry search failed.");
        return await safeParseJson(res);
    },

    saveQuestionsBulk: async (certId: string, questions: BackendQuestion[]) => {
        const res = await fetch(`${API_BASE_URL}/${certId}/questions/bulk`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ questions })
        });
        if (!res.ok) {
            const err = await safeParseJson(res).catch(() => ({ error: 'Registry Sync Aborted' }));
            throw new Error(err.error || "Failed to commit bulk items.");
        }
        return await safeParseJson(res);
    },

    updateQuestion: async (certId: string, id: string, data: Partial<BackendQuestion>) => {
        const res = await fetch(`${API_BASE_URL}/${certId}/questions/${id}`, {
            method: 'PUT',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Update failed");
        return await safeParseJson(res);
    },

    deleteQuestionsBulk: async (certId: string, ids: string[]) => {
        const res = await fetch(`${API_BASE_URL}/${certId}/questions/bulk`, {
            method: 'DELETE',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ ids })
        });
        if (!res.ok) throw new Error("Bulk purge failed.");
        return await safeParseJson(res);
    },

    getQuestionDifficultyStats: async (certId: string) => {
        const res = await fetch(`${API_BASE_URL}/${certId}/questions/difficulty-stats`, {
            headers: StorageService.getAuthHeader()
        });
        if (!res.ok) throw new Error("Failed to retrieve difficulty metrics.");
        return await safeParseJson(res);
    },

    migrateQuestionAi: async (certId: string, questionIdOrObj: string | BackendQuestion, style: string = 'Prometric', customInstruction?: string) => {
        const body: any = { style, customInstruction };
        if (typeof questionIdOrObj === 'string') {
            body.questionId = questionIdOrObj;
        } else {
            body.questionId = questionIdOrObj._id;
            body.question = questionIdOrObj;
        }

        const res = await fetch(`${API_BASE_URL}/${certId}/questions/ai-migrate`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify(body)
        });
        if (!res.ok) {
            const err = await safeParseJson(res).catch(() => ({ error: 'AI Upgrade Request Failed' }));
            throw new Error(err.error || "Failed to upgrade registry node.");
        }
        return await safeParseJson(res);
    },

    migrateQuestionsBulkAi: async (certId: string, questionIds: string[], style: string = 'Prometric', customInstruction?: string, autoCommit: boolean = false) => {
        const res = await fetch(`${API_BASE_URL}/${certId}/questions/ai-bulk-migrate`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ questionIds, style, customInstruction, autoCommit })
        });
        if (!res.ok) {
            const err = await safeParseJson(res).catch(() => ({ error: 'Bulk AI Upgrade Request Failed' }));
            throw new Error(err.error || "Failed to bulk upgrade registry nodes.");
        }
        return await safeParseJson(res);
    },

    getStudyModulesSummary: async (certId: string = 'cna'): Promise<StudyModule[]> => {
        const res = await fetch(`${API_BASE_URL}/${certId}/study-modules?summary=true`);
        const data = await safeParseJson(res);
        return data.items || [];
    },

    getStudyModuleById: async (certId: string, id: string): Promise<StudyModule> => {
        const res = await fetch(`${API_BASE_URL}/${certId}/study-modules/${id}`);
        if (!res.ok) throw new Error("Failed to retrieve study module logic from registry.");
        return await safeParseJson(res);
    },

    getStudyModules: async (certId: string = 'cna'): Promise<StudyModule[]> => {
        const res = await fetch(`${API_BASE_URL}/${certId}/study-modules`);
        const data = await safeParseJson(res);
        return data.items || [];
    },

    saveStudyModule: async (certId: string, module: Partial<StudyModule>) => {
        const res = await fetch(`${API_BASE_URL}/${certId}/study-modules/${module.id}`, {
            method: 'PUT',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify(module)
        });
        if (!res.ok) throw new Error("Individual study module commit failed.");
        return await safeParseJson(res);
    },

    deleteStudyModule: async (certId: string, id: string) => {
        const res = await fetch(`${API_BASE_URL}/${certId}/study-modules/${id}`, {
            method: 'DELETE',
            headers: StorageService.getAuthHeader()
        });
        if (!res.ok) throw new Error("Module purge failed.");
        return await safeParseJson(res);
    },

    getSkillsSummary: async (certId: string = 'cna'): Promise<SkillSlide[]> => {
        const targetCertId = (certId || 'cna').toLowerCase().trim();
        const localKey = `cna_skills_summary_${targetCertId}`;

        // Helper to ensure video and procedural step flags are preserved
        const enrichSummaryItems = (list: SkillSlide[]): SkillSlide[] => {
            return list.map(s => {
                const staticRef = CNA_SKILLS.find(cs => cs.id === s.id);
                const hasVideo = Boolean(
                    s.hasVideo || 
                    s.videoUrl || 
                    staticRef?.hasVideo ||
                    (Array.isArray(s.slides) && s.slides.some(sl => sl.type === 'embedded' || sl.data?.embedUrl || sl.data?.embedCode || sl.data?.videoUrl || sl.data?.youtubeUrl)) ||
                    (Array.isArray(staticRef?.slides) && staticRef.slides.some(sl => sl.type === 'embedded' || sl.data?.embedUrl || sl.data?.embedCode || sl.data?.videoUrl || sl.data?.youtubeUrl))
                );
                const videoUrl = s.videoUrl || staticRef?.videoUrl || '';
                const procedureStepsCount = s.procedureStepsCount || (Array.isArray(s.slides) ? s.slides.filter(sl => sl.type === 'procedure').length : 0) || staticRef?.procedureStepsCount || 0;
                const hasCriticals = Boolean(
                    s.hasCriticals || 
                    staticRef?.hasCriticals ||
                    (Array.isArray(s.slides) && s.slides.some(sl => sl.type === 'criticals' || sl.data?.criticalPoints)) ||
                    (Array.isArray(staticRef?.slides) && staticRef.slides.some(sl => sl.type === 'criticals' || sl.data?.criticalPoints))
                );
                const isServerHydrated = isSkillHydrated(s);
                const slides = isServerHydrated
                    ? s.slides
                    : (staticRef && isSkillHydrated(staticRef)
                        ? staticRef.slides
                        : ((Array.isArray(s.slides) && s.slides.length > 0) ? s.slides : []));

                return {
                    ...s,
                    hasVideo,
                    videoUrl,
                    procedureStepsCount,
                    hasCriticals,
                    slides
                };
            });
        };

        // 1. Read existing local items
        let localSkills: SkillSlide[] = [];
        try {
            const raw = localStorage.getItem(localKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed) && parsed.length > 0) localSkills = enrichSummaryItems(parsed);
            }
        } catch (e) {}

        // 2. Fetch from server with abort timeout
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 3500);
            const res = await fetch(`${API_BASE_URL}/${targetCertId}/skills?summary=true`, { signal: controller.signal });
            clearTimeout(timeoutId);
            if (res.ok) {
                const data = await safeParseJson(res);
                if (data.items && Array.isArray(data.items) && data.items.length > 0) {
                    const enriched = enrichSummaryItems(data.items);
                    try {
                        localStorage.setItem(localKey, JSON.stringify(enriched));
                    } catch (e) {}
                    return enriched;
                }
            }
        } catch (e) {
            console.warn("Skills summary server fetch fallback:", e);
        }

        if (localSkills.length > 0) return enrichSummaryItems(localSkills);

        // Fallback to CNA_SKILLS if cna
        return targetCertId === 'cna' ? enrichSummaryItems(CNA_SKILLS) : [];
    },

    getSkillById: async (certId: string, id: string): Promise<SkillSlide> => {
        const targetCertId = (certId || 'cna').toLowerCase().trim();
        const localKey = `cna_skill_${targetCertId}_${id}`;
        
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 3500);
            const res = await fetch(`${API_BASE_URL}/${targetCertId}/skills/${id}`, { signal: controller.signal });
            clearTimeout(timeoutId);
            if (res.ok) {
                const data = await safeParseJson(res);
                const resolvedData = (!isSkillHydrated(data) && staticMatch && isSkillHydrated(staticMatch))
                    ? { ...data, slides: staticMatch.slides }
                    : data;
                try {
                    localStorage.setItem(localKey, JSON.stringify(resolvedData));
                } catch (e) {}
                return resolvedData;
            }
        } catch (e) {}

        // Local cache fallback (validate that it is actually hydrated)
        try {
            const raw = localStorage.getItem(localKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (isSkillHydrated(parsed)) return parsed;
            }
        } catch (e) {}

        // Static fallback
        const staticMatch = CNA_SKILLS.find(s => s.id === id);
        if (staticMatch) return staticMatch;

        throw new Error("Failed to retrieve skill detail from registry.");
    },

    saveSkill: async (certId: string, skill: Partial<SkillSlide>) => {
        const targetCertId = (certId || 'cna').toLowerCase().trim();
        const summaryKey = `cna_skills_summary_${targetCertId}`;
        const itemKey = `cna_skill_${targetCertId}_${skill.id}`;

        // 1. Immediately update localStorage for instant optimistic response
        try {
            localStorage.setItem(itemKey, JSON.stringify(skill));
            const rawSummary = localStorage.getItem(summaryKey);
            let summaries: any[] = [];
            if (rawSummary) {
                try { summaries = JSON.parse(rawSummary); } catch (e) {}
            }
            const sIdx = summaries.findIndex(s => s && s.id === skill.id);
            if (sIdx >= 0) {
                summaries[sIdx] = { ...summaries[sIdx], ...skill };
            } else {
                summaries.push(skill);
            }
            localStorage.setItem(summaryKey, JSON.stringify(summaries));
        } catch (e) {}

        // 2. Dispatch events for real-time reactivity across views
        try {
            window.dispatchEvent(new CustomEvent('skills-updated', {
                detail: { certId: targetCertId, skill }
            }));
        } catch (e) {}

        // 3. Persist to server
        try {
            const res = await fetch(`${API_BASE_URL}/${targetCertId}/skills/${skill.id}`, {
                method: 'PUT',
                headers: {
                    ...StorageService.getAuthHeader(),
                    'Content-Type': 'application/json',
                    'x-admin-request': 'true'
                },
                body: JSON.stringify(skill)
            });
            if (res.ok) {
                return await safeParseJson(res);
            }
        } catch (e) {
            console.warn("Skill save server call fallback, saved locally:", e);
        }
        return skill;
    },

    toggleSkillSimulator: async (certId: string, skillId: string, isSimulatorEnabled: boolean) => {
        const res = await fetch(`${API_BASE_URL}/${certId}/skills/${skillId}/toggle-simulator`, {
            method: 'PATCH',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ isSimulatorEnabled })
        });
        if (!res.ok) {
            const existing = await StorageService.getSkillById(certId, skillId);
            if (existing) {
                existing.isSimulatorEnabled = isSimulatorEnabled;
                return await StorageService.saveSkill(certId, existing);
            }
            throw new Error("Failed to update skill simulator flag.");
        }
        return await safeParseJson(res);
    },

    deleteSkill: async (certId: string, id: string) => {
        const res = await fetch(`${API_BASE_URL}/${certId}/skills/${id}`, {
            method: 'DELETE',
            headers: StorageService.getAuthHeader()
        });
        if (!res.ok) throw new Error("Skill purge failed.");
        return await safeParseJson(res);
    },

    getScenarioChallenges: async (certId: string, params: Record<string, any> = {}): Promise<any> => {
        const cleaned = cleanParams(params);
        const query = new URLSearchParams(cleaned).toString();
        const res = await fetch(`${API_BASE_URL}/${certId}/scenarios?${query}`);
        return await safeParseJson(res);
    },

    saveScenarioChallenge: async (certId: string, challenge: Partial<ScenarioChallenge>) => {
        const url = `${API_BASE_URL}/${certId}/scenarios`;
        const res = await fetch(url, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify(challenge)
        });
        
        if (!res.ok) {
            const result = await safeParseJson(res).catch(() => ({ error: `Server logic error (${res.status})` }));
            throw new Error(result.error || "Scenario Registry commit failed.");
        }
        return await safeParseJson(res);
    },

    saveScenariosBulk: async (certId: string, scenarios: Partial<ScenarioChallenge>[]) => {
        const res = await fetch(`${API_BASE_URL}/${certId}/scenarios/bulk`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ scenarios })
        });
        if (!res.ok) {
            const result = await safeParseJson(res).catch(() => ({ error: 'Bulk Registry Sync Aborted' }));
            throw new Error(result.error || "Bulk Scenario Registry commit failed.");
        }
        return await safeParseJson(res);
    },

    deleteScenarioChallenge: async (certId: string, id: string) => {
        const res = await fetch(`${API_BASE_URL}/${certId}/scenarios/${id}`, {
            method: 'DELETE',
            headers: StorageService.getAuthHeader()
        });
        if (!res.ok) throw new Error("Purge request failed.");
    },

    startRemoteExam: async (certId: string, stateCode: string, exclusive: boolean = false, fixedIndex: number | null = null): Promise<ExamSession> => {
        const res = await fetch(`${API_BASE_URL}/${certId}/exam/start`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ stateCode, exclusive, fixedIndex })
        });
        return await safeParseJson(res);
    },

    getFixedTestRoadmap: async (certId: string, stateCode: string): Promise<any> => {
        const params = new URLSearchParams({ stateCode }).toString();
        const res = await fetch(`${API_BASE_URL}/${certId}/fixed-test-roadmap?${params}`);
        if (!res.ok) throw new Error("Roadmap sync failure");
        return await safeParseJson(res);
    },

    getLeaderboardData: async (certId: string, userId: string, state: string): Promise<any> => {
        const params = new URLSearchParams({ userId, state }).toString();
        const res = await fetch(`${API_BASE_URL}/${certId}/leaderboard?${params}`);
        if (!res.ok) throw new Error("Leaderboard sync failure");
        return await safeParseJson(res);
    },

    getGlobalUsers: async (params: Record<string, any> = {}): Promise<{items: User[], total: number, page: number, limit: number}> => {
        const cleaned = cleanParams(params);
        const query = new URLSearchParams(cleaned).toString();
        const res = await fetch(`${API_BASE_URL}/users?${query}`, { headers: StorageService.getAuthHeader() });
        const data = await safeParseJson(res);
        const users = data.items || [];
        users.forEach((u: User) => {
            StorageService._userCache.set(u.id, u);
            if (u.email) StorageService._userCache.set(u.email, u);
        });
        return data;
    },

    registerUser: async (email: string, planTier: string = 'free', wixId?: string, state?: string, certId: string = 'cna', name?: string): Promise<User> => {
        const res = await fetch(`${API_BASE_URL}/users/register`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ email, planTier, id: wixId, state, certId, name })
        });
        const user = await safeParseJson(res);
        StorageService._userCache.set(user.id, user);
        if (user.email) StorageService._userCache.set(user.email, user);
        return user;
    },

    loginUser: async (email: string, password?: string): Promise<any> => {
        const deviceToken = window.localStorage.getItem('admin_trusted_device_token') || '';
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (deviceToken) {
            headers['x-device-token'] = deviceToken;
        }
        const res = await fetch(`${API_BASE_URL}/users/login`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ email, password })
        });
        if (!res.ok) {
            const errData = await safeParseJson(res).catch(() => ({}));
            throw new Error(errData.error || 'Authentication Protocol Rejected');
        }
        const user = await safeParseJson(res);
        if (user.require2FA) {
            return user;
        }
        StorageService._userCache.set(user.id, user);
        if (user.email) StorageService._userCache.set(user.email, user);
        return user;
    },

    getSimulatorScenarios: async (certId: string = 'cna'): Promise<SimulatorScenario[]> => {
        const res = await fetch(`${API_BASE_URL}/${certId}/simulator-scenarios`);
        const data = await safeParseJson(res);
        return data.items || [];
    },

    saveSimulatorScenarios: async (certId: string, scenarios: SimulatorScenario[]) => {
        const res = await fetch(`${API_BASE_URL}/${certId}/simulator-scenarios`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ scenarios })
        });
        if (!res.ok) throw new Error("Simulator scenarios save failed");
        return await safeParseJson(res);
    },

    getClinicalSimulators: async (certId: string = 'cna'): Promise<ClinicalSimulatorScenario[]> => {
        const targetCertId = (certId || 'cna').toLowerCase().trim();

        // 1. Read existing local items
        let localItems: ClinicalSimulatorScenario[] = [];
        try {
            const localKey = `cna_clinical_sims_${targetCertId}`;
            const localStored = localStorage.getItem(localKey);
            if (localStored) {
                const parsed = JSON.parse(localStored);
                if (Array.isArray(parsed)) {
                    localItems = parsed.map(p => normalizeClinicalSimulatorScenario(p, targetCertId));
                }
            }
        } catch (e) {}

        // 2. Fetch server items with timeout
        let serverItems: ClinicalSimulatorScenario[] = [];
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 6000);
            const res = await fetch(`${API_BASE_URL}/${targetCertId}/clinical-simulators`, {
                signal: controller.signal
            });
            clearTimeout(timeoutId);
            if (res.ok) {
                const data = await safeParseJson(res);
                if (data.items && Array.isArray(data.items)) {
                    serverItems = data.items.map((it: any) => normalizeClinicalSimulatorScenario(it, targetCertId));
                }
            }
        } catch (e) {
            console.warn("Server clinical simulator fetch fallback:", e);
        }

        // 3. Defaults
        const defaultItems = DEFAULT_CLINICAL_SIMULATORS.filter(
            s => {
                const sCert = (s.certId || 'cna').toLowerCase().trim();
                return sCert === targetCertId;
            }
        ).map(d => normalizeClinicalSimulatorScenario(d, targetCertId));

        // 4. Merge map: defaults first, then localItems, then server items take authoritative precedence
        const combinedMap = new Map<string, ClinicalSimulatorScenario>();
        for (const s of defaultItems) {
            if (s && s.id) combinedMap.set(s.id, s);
        }
        for (const s of localItems) {
            if (s && s.id) combinedMap.set(s.id, s);
        }
        for (const s of serverItems) {
            if (s && s.id) combinedMap.set(s.id, s);
        }

        const allItems = Array.from(combinedMap.values());

        // Sort so user-created scenarios (those starting with 'sim_' or not in default list) are placed at the TOP
        const defaultIdSet = new Set(defaultItems.map(d => d.id));
        const userCreatedScenarios = allItems.filter(s => s && s.id && (!defaultIdSet.has(s.id) || s.id.startsWith('sim_')));
        const standardCurriculumScenarios = allItems.filter(s => s && s.id && defaultIdSet.has(s.id) && !s.id.startsWith('sim_'));

        const sorted = [...userCreatedScenarios, ...standardCurriculumScenarios];

        // Keep local cache synced with full merged array
        try {
            const localKey = `cna_clinical_sims_${targetCertId}`;
            localStorage.setItem(localKey, JSON.stringify(sorted));
        } catch (e) {}

        return sorted;
    },

    getClinicalSimulatorById: async (certId: string, id: string): Promise<ClinicalSimulatorScenario | null> => {
        const targetCertId = (certId || 'cna').toLowerCase().trim();
        try {
            const res = await fetch(`${API_BASE_URL}/${targetCertId}/clinical-simulators/${id}`);
            if (res.ok) {
                const data = await safeParseJson(res);
                if (data && data.id) {
                    return normalizeClinicalSimulatorScenario(data, targetCertId);
                }
            }
        } catch (e) {}
        const all = await StorageService.getClinicalSimulators(targetCertId);
        const match = all.find(s => s.id === id);
        return match ? normalizeClinicalSimulatorScenario(match, targetCertId) : null;
    },

    getClinicalSimulatorForSkill: async (certId: string, skillId: string): Promise<ClinicalSimulatorScenario | null> => {
        const targetCertId = (certId || 'cna').toLowerCase().trim();
        try {
            const res = await fetch(`${API_BASE_URL}/${targetCertId}/clinical-simulators?skillId=${encodeURIComponent(skillId)}`);
            if (res.ok) {
                const data = await safeParseJson(res);
                if (data.items && data.items.length > 0) {
                    return normalizeClinicalSimulatorScenario(data.items[0], targetCertId);
                }
            }
        } catch (e) {}
        const all = await StorageService.getClinicalSimulators(targetCertId);
        const match = all.find(s => s.skillId === skillId);
        return match ? normalizeClinicalSimulatorScenario(match, targetCertId) : null;
    },

    saveClinicalSimulator: async (certId: string, scenario: ClinicalSimulatorScenario): Promise<any> => {
        const targetCertId = (certId || scenario.certId || 'cna').toLowerCase().trim();
        const cleanScenario = normalizeClinicalSimulatorScenario(scenario, targetCertId);

        // 1. Save to localStorage as immediate client cache
        const localKey = `cna_clinical_sims_${targetCertId}`;
        try {
            const existingRaw = localStorage.getItem(localKey);
            let existing: ClinicalSimulatorScenario[] = [];
            if (existingRaw) {
                try {
                    const parsed = JSON.parse(existingRaw);
                    if (Array.isArray(parsed)) {
                        existing = parsed.map(p => normalizeClinicalSimulatorScenario(p, targetCertId));
                    }
                } catch (e) {}
            }
            const index = existing.findIndex(s => s && s.id === cleanScenario.id);
            if (index >= 0) {
                existing[index] = cleanScenario;
            } else {
                existing.unshift(cleanScenario); // Place newest at top!
            }
            localStorage.setItem(localKey, JSON.stringify(existing));
            localStorage.setItem(`cna_last_active_sim_${targetCertId}`, cleanScenario.id);
        } catch (e) {
            console.warn("Local storage write error:", e);
        }

        // 2. Dispatch event for instant reactive updates across listeners
        try {
            window.dispatchEvent(new CustomEvent('clinical-simulators-updated', {
                detail: { certId: targetCertId, scenario: cleanScenario }
            }));
        } catch (e) {}

        // 3. Persist to server (try POST / PUT)
        try {
            const res = await fetch(`${API_BASE_URL}/${targetCertId}/clinical-simulators`, {
                method: 'POST',
                headers: {
                    ...StorageService.getAuthHeader(),
                    'Content-Type': 'application/json',
                    'x-admin-request': 'true'
                },
                body: JSON.stringify(cleanScenario)
            });
            if (res.ok) {
                const saved = await safeParseJson(res);
                return normalizeClinicalSimulatorScenario(saved || cleanScenario, targetCertId);
            }
        } catch (e) {
            console.warn("Server clinical simulator save fallback, persisted locally:", e);
        }
        return cleanScenario;
    },

    deleteClinicalSimulator: async (certId: string, id: string): Promise<any> => {
        const targetCertId = (certId || 'cna').toLowerCase().trim();
        try {
            const localKey = `cna_clinical_sims_${targetCertId}`;
            const existingRaw = localStorage.getItem(localKey);
            if (existingRaw) {
                const existing: ClinicalSimulatorScenario[] = JSON.parse(existingRaw);
                const filtered = existing.filter(s => s.id !== id);
                localStorage.setItem(localKey, JSON.stringify(filtered));
            }
            const lastActive = localStorage.getItem(`cna_last_active_sim_${targetCertId}`);
            if (lastActive === id) {
                localStorage.removeItem(`cna_last_active_sim_${targetCertId}`);
            }
        } catch (e) {}

        try {
            window.dispatchEvent(new CustomEvent('clinical-simulators-updated', {
                detail: { certId: targetCertId, deletedId: id }
            }));
        } catch (e) {}

        try {
            const res = await fetch(`${API_BASE_URL}/${targetCertId}/clinical-simulators/${id}`, {
                method: 'DELETE',
                headers: {
                    ...StorageService.getAuthHeader(),
                    'x-admin-request': 'true'
                }
            });
            return await safeParseJson(res);
        } catch (e) {
            return { success: true };
        }
    },

    generateClinicalSimulatorBackend: async (
        certId: string, 
        skillId: string, 
        skillTitle?: string, 
        customContext?: string
    ): Promise<ClinicalSimulatorScenario> => {
        const res = await fetch(`${API_BASE_URL}/${certId}/clinical-simulators/generate`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ skillId, skillTitle, customContext })
        });
        if (!res.ok) {
            const errData = await safeParseJson(res);
            throw new Error(errData?.error || "Backend scenario generation failed.");
        }
        const data = await safeParseJson(res);
        return data.scenario;
    },

    getPodcasts: async (certId: string, includePrivate: boolean = false): Promise<Podcast[]> => {
        const params = new URLSearchParams();
        if (includePrivate) params.append('includePrivate', 'true');
        const res = await fetch(`${API_BASE_URL}/${certId}/podcasts?${params.toString()}`);
        const data = await safeParseJson(res);
        return data.items || [];
    },

    savePodcast: async (certId: string, podcast: Partial<Podcast>) => {
        const method = podcast._id ? 'PUT' : 'POST';
        const url = podcast._id ? `${API_BASE_URL}/podcasts/${podcast._id}` : `${API_BASE_URL}/podcasts`;
        const res = await fetch(url, {
            method,
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify(podcast)
        });
        if (!res.ok) throw new Error("Podcast save failed");
        return await safeParseJson(res);
    },

    savePodcastsBulk: async (certId: string, podcasts: Partial<Podcast>[]) => {
        const res = await fetch(`${API_BASE_URL}/podcasts/bulk`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ podcasts })
        });
        if (!res.ok) throw new Error("Bulk podcasts save failed");
        return await safeParseJson(res);
    },

    deletePodcast: async (certId: string, id: string) => {
        await fetch(`${API_BASE_URL}/podcasts/${id}`, {
            method: 'DELETE',
            headers: StorageService.getAuthHeader()
        });
    },

    // --- FACULTY ---
    getFaculty: async (): Promise<any[]> => {
        const res = await fetch(`${API_BASE_URL}/faculty`);
        const data = await safeParseJson(res);
        return data.items || [];
    },

    saveFaculty: async (faculty: any) => {
        const method = faculty._id ? 'PUT' : 'POST';
        const url = faculty._id ? `${API_BASE_URL}/faculty/${faculty._id}` : `${API_BASE_URL}/faculty`;
        const res = await fetch(url, {
            method,
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify(faculty)
        });
        if (!res.ok) throw new Error("Faculty save failed");
        return await safeParseJson(res);
    },

    deleteFaculty: async (id: string) => {
        const res = await fetch(`${API_BASE_URL}/faculty/${id}`, {
            method: 'DELETE',
            headers: StorageService.getAuthHeader()
        });
        if (!res.ok) throw new Error("Faculty deletion failed");
    },

    submitFeedback: async (feedback: { category: string, message: string, state?: string, certId?: string, metadata?: any }) => {
        const res = await fetch(`${API_BASE_URL}/feedback`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify(feedback)
        });
        if (!res.ok) throw new Error("Feedback submission failure");
        return await safeParseJson(res);
    },

    // --- TWO-FACTOR AUTHENTICATION & TRUSTED DEVICES ---
    verify2FA: async (params: { preAuthToken: string; code: string; trustDevice: boolean; deviceName?: string }): Promise<any> => {
        const res = await fetch(`${API_BASE_URL}/auth/2fa/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(params)
        });
        if (!res.ok) {
            const errData = await safeParseJson(res).catch(() => ({}));
            throw new Error(errData.error || '2FA Verification Failed');
        }
        const data = await safeParseJson(res);
        if (data.deviceToken) {
            window.localStorage.setItem('admin_trusted_device_token', data.deviceToken);
        }
        if (data.token) {
            window.localStorage.setItem('wix_auth_token', data.token);
        }
        if (data.user) {
            StorageService._userCache.set(data.user.id, data.user);
            if (data.user.email) StorageService._userCache.set(data.user.email, data.user);
        }
        return data;
    },

    get2FAStatus: async (): Promise<TwoFactorStatus> => {
        const res = await fetch(`${API_BASE_URL}/auth/2fa/status`, {
            headers: StorageService.getAuthHeader()
        });
        if (!res.ok) throw new Error("Failed to load 2FA security status");
        return await safeParseJson(res);
    },

    setup2FA: async (): Promise<{ secret: string; otpauthUrl: string; qrCodeDataUrl: string }> => {
        const res = await fetch(`${API_BASE_URL}/auth/2fa/setup`, {
            method: 'POST',
            headers: StorageService.getAuthHeader()
        });
        if (!res.ok) {
            const err = await safeParseJson(res).catch(() => ({}));
            throw new Error(err.error || "Failed to initialize 2FA setup");
        }
        return await safeParseJson(res);
    },

    confirm2FASetup: async (code: string, secret: string): Promise<{ success: boolean; backupCodes: string[] }> => {
        const res = await fetch(`${API_BASE_URL}/auth/2fa/confirm-setup`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ code, secret })
        });
        if (!res.ok) {
            const err = await safeParseJson(res).catch(() => ({}));
            throw new Error(err.error || "Failed to confirm 2FA setup");
        }
        return await safeParseJson(res);
    },

    disable2FA: async (password?: string, code?: string): Promise<{ success: boolean; message?: string }> => {
        const res = await fetch(`${API_BASE_URL}/auth/2fa/disable`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ password, code })
        });
        if (!res.ok) {
            const err = await safeParseJson(res).catch(() => ({}));
            throw new Error(err.error || "Failed to disable 2FA");
        }
        window.localStorage.removeItem('admin_trusted_device_token');
        return await safeParseJson(res);
    },

    regenerateBackupCodes: async (code: string): Promise<{ success: boolean; backupCodes: string[] }> => {
        const res = await fetch(`${API_BASE_URL}/auth/2fa/regenerate-backup-codes`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify({ code })
        });
        if (!res.ok) {
            const err = await safeParseJson(res).catch(() => ({}));
            throw new Error(err.error || "Failed to regenerate backup recovery codes");
        }
        return await safeParseJson(res);
    },

    getTrustedDevices: async (): Promise<{ devices: TrustedDeviceInfo[] }> => {
        const res = await fetch(`${API_BASE_URL}/auth/2fa/devices`, {
            headers: StorageService.getAuthHeader()
        });
        if (!res.ok) throw new Error("Failed to load trusted devices");
        return await safeParseJson(res);
    },

    revokeTrustedDevice: async (id: string): Promise<void> => {
        const res = await fetch(`${API_BASE_URL}/auth/2fa/devices/${id}`, {
            method: 'DELETE',
            headers: StorageService.getAuthHeader()
        });
        if (!res.ok) {
            const err = await safeParseJson(res).catch(() => ({}));
            throw new Error(err.error || "Failed to revoke device");
        }
    },

    revokeAllTrustedDevices: async (): Promise<void> => {
        const res = await fetch(`${API_BASE_URL}/auth/2fa/devices/revoke-all`, {
            method: 'POST',
            headers: StorageService.getAuthHeader()
        });
        if (!res.ok) {
            const err = await safeParseJson(res).catch(() => ({}));
            throw new Error(err.error || "Failed to revoke devices");
        }
        window.localStorage.removeItem('admin_trusted_device_token');
    },

    // --- EXAM CONFIGS & 50-STATE PROVISIONING ---
    getExamConfigs: async (certId: string): Promise<any[]> => {
        const res = await fetch(`${API_BASE_URL}/exam-configs?certId=${encodeURIComponent(certId)}`, {
            headers: StorageService.getAuthHeader()
        });
        if (!res.ok) throw new Error("Exam configuration registry unavailable");
        const data = await safeParseJson(res);
        return data.items || [];
    },

    saveExamConfig: async (config: any): Promise<any> => {
        const method = config._id ? 'PUT' : 'POST';
        const url = config._id ? `${API_BASE_URL}/exam-configs/${config._id}` : `${API_BASE_URL}/exam-configs`;
        const res = await fetch(url, {
            method,
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify(config)
        });
        if (!res.ok) {
            const err = await safeParseJson(res).catch(() => ({}));
            throw new Error(err.error || `Failed to commit exam protocol (HTTP ${res.status})`);
        }
        return await safeParseJson(res);
    },

    deleteExamConfig: async (id: string): Promise<void> => {
        const res = await fetch(`${API_BASE_URL}/exam-configs/${id}`, {
            method: 'DELETE',
            headers: StorageService.getAuthHeader()
        });
        if (!res.ok) {
            const err = await safeParseJson(res).catch(() => ({}));
            throw new Error(err.error || "Failed to purge exam protocol");
        }
    },

    repairExamConfigIndexes: async (): Promise<any> => {
        const res = await fetch(`${API_BASE_URL}/exam-configs/repair-indexes`, {
            method: 'POST',
            headers: StorageService.getAuthHeader()
        });
        if (!res.ok) {
            const err = await safeParseJson(res).catch(() => ({}));
            throw new Error(err.error || "Failed to repair exam configuration registry indexes");
        }
        return await safeParseJson(res);
    },

    bulkGenerate50StatesExamConfigs: async (params: {
        certId: string;
        overwriteExisting?: boolean;
        vendor?: string;
        timeLimitMinutes?: number;
        questionCount?: number;
        passingPercent?: number;
    }): Promise<{ success: boolean; totalJurisdictions: number; created: number; updated: number; message: string; items: any[] }> => {
        const res = await fetch(`${API_BASE_URL}/exam-configs/bulk-generate-50-states`, {
            method: 'POST',
            headers: StorageService.getAuthHeader(),
            body: JSON.stringify(params)
        });
        if (!res.ok) {
            const err = await safeParseJson(res).catch(() => ({}));
            throw new Error(err.error || `Bulk jurisdiction provisioning failed (HTTP ${res.status})`);
        }
        return await safeParseJson(res);
    }
};

